import { FolderOpenDot, PencilLine, Trash2 } from 'lucide-react';
import { ActionIconButton } from '../common';
import { formatPeso } from '../../utils/formatters';
import EntityAvatar from './EntityAvatar';
import EntityStatusBadge from './EntityStatusBadge';

function EntityTable({ records, onEditRecord, onDeleteRecord = null, entityLabel = 'Supplier' }) {
  const showsSacksMark = entityLabel === 'Supplier';
  const showsCashAdvanceColumns = entityLabel === 'Supplier';
  const showsBranchLocations = entityLabel === 'Customer';
  const emptyColSpan = showsSacksMark ? 8 : 6;

  return (
    <div className="overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>Photo</TableHeading>
              <TableHeading>Company</TableHeading>
              <TableHeading>Contact No.</TableHeading>
              {showsSacksMark ? <TableHeading>Sacks Mark</TableHeading> : null}
              {showsBranchLocations ? <TableHeading>Branch/es</TableHeading> : null}
              {showsCashAdvanceColumns ? (
                <>
                  <TableHeading>Total CA/Loan</TableHeading>
                  <TableHeading>Remaining CA/Loan</TableHeading>
                </>
              ) : null}
              <TableHeading>Status</TableHeading>
              <TableHeading>Actions</TableHeading>
            </tr>
          </thead>
          <tbody>
            {records.length ? (
              records.map((record) => (
                <tr key={record.id} className="border-t border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-surface-soft)]">
                  <TableCell>
                    <EntityAvatar
                      photoUrl={record.photoUrl}
                      name={record.companyName}
                      entityLabel={entityLabel}
                      className="h-12 w-12"
                      textClassName="text-[0.95rem] font-semibold"
                    />
                  </TableCell>
                  <TableCell>{record.companyName}</TableCell>
                  <TableCell>{record.contactNumber || '-'}</TableCell>
                  {showsSacksMark ? <TableCell>{record.sacksMark || '-'}</TableCell> : null}
                  {showsBranchLocations ? (
                    <TableCell>{formatBranchLocations(record.branchLocations)}</TableCell>
                  ) : null}
                  {showsCashAdvanceColumns ? (
                    <>
                      <TableCell>
                        <CashAdvanceValue value={record.totalCashAdvance} />
                      </TableCell>
                      <TableCell>
                        <CashAdvanceValue value={record.remainingCashAdvance} />
                      </TableCell>
                    </>
                  ) : null}
                  <TableCell>
                    <EntityStatusBadge status={record.status} />
                  </TableCell>
                  <TableCell>
                    <ActionButtons
                      record={record}
                      onEditRecord={onEditRecord}
                      onDeleteRecord={onDeleteRecord}
                    />
                  </TableCell>
                </tr>
              ))
            ) : (
              <tr className="border-t border-[var(--theme-border-soft)]">
                <td className="px-6 py-14" colSpan={emptyColSpan}>
                  <EmptyState entityLabel={entityLabel} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 p-4 md:hidden">
        {records.length ? (
          records.map((record) => (
            <article
              key={record.id}
              className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-3 transition duration-150 hover:-translate-y-0.5 hover:bg-[var(--theme-surface-soft)] hover:shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.11)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <EntityAvatar
                    photoUrl={record.photoUrl}
                    name={record.companyName}
                    entityLabel={entityLabel}
                    className="h-12 w-12 shrink-0"
                    textClassName="text-[0.95rem] font-semibold"
                  />
                  <div>
                    <strong className="block text-[1rem] text-[var(--theme-text)]">{record.companyName}</strong>
                  </div>
                </div>
                <EntityStatusBadge status={record.status} />
              </div>

              <div className="mt-3 grid gap-1.5 text-[0.88rem] text-[var(--theme-text-subtle)]">
                <RecordMeta label="Contact No." value={record.contactNumber || '-'} />
                {showsSacksMark ? <RecordMeta label="Sacks Mark" value={record.sacksMark || '-'} /> : null}
                {showsBranchLocations ? (
                  <RecordMeta label="Branch/es" value={formatBranchLocations(record.branchLocations)} />
                ) : null}
                {showsCashAdvanceColumns ? (
                  <>
                    <RecordMeta label="Total CA/Loan" value={<CashAdvanceValue value={record.totalCashAdvance} />} />
                    <RecordMeta label="Remaining CA/Loan" value={<CashAdvanceValue value={record.remainingCashAdvance} />} />
                  </>
                ) : null}
                <RecordMeta label="Status" value={<EntityStatusBadge status={record.status} />} />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[var(--theme-text-muted)]">Actions</span>
                  <ActionButtons
                    record={record}
                    onEditRecord={onEditRecord}
                    onDeleteRecord={onDeleteRecord}
                  />
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState entityLabel={entityLabel} />
          </div>
        )}
      </div>
    </div>
  );
}

function TableHeading({ children }) {
  return (
    <th className="whitespace-nowrap px-3 py-2.5 text-left text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return (
    <td className="whitespace-nowrap px-3 py-2 align-middle text-[0.84rem] text-[#222222] lg:px-4">
      {children}
    </td>
  );
}

function RecordMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <div className="text-right font-medium text-[#2a2a2a]">{value}</div>
    </div>
  );
}

function PlaceholderValue() {
  return <span className="text-[#b1a59b]">-</span>;
}

function CashAdvanceValue({ value }) {
  if (!Number(value)) {
    return <PlaceholderValue />;
  }

  return <span>{formatPeso(value)}</span>;
}

function formatBranchLocations(branchLocations) {
  const branches = Array.isArray(branchLocations?.branches)
    ? branchLocations.branches.filter(Boolean)
    : [];

  return branches.length ? branches.join(', ') : <PlaceholderValue />;
}

function ActionButtons({ record, onEditRecord, onDeleteRecord }) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      <ActionIconButton
        onClick={() => onEditRecord(record)}
        label={`Edit ${record.companyName}`}
      >
        <PencilLine className="h-4 w-4" aria-hidden="true" />
      </ActionIconButton>
      {onDeleteRecord ? (
        <ActionIconButton
          onClick={() => onDeleteRecord(record)}
          label={`Delete ${record.companyName}`}
          tone="danger"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </ActionIconButton>
      ) : null}
    </div>
  );
}

function EmptyState({ entityLabel }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <FolderOpenDot className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        No {entityLabel.toLowerCase()}s found
      </strong>
    </div>
  );
}

export default EntityTable;
